import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Form, Input, Spin, Typography } from 'antd';
// Api
import {
  ADD_PSYCHOSOCIAL_KEYWORD,
  EDIT_PSYCHOSOCIAL_KEYWORD,
} from 'api/psychosocialKeyword/mutations';
import { GET_ALL_PSYCHOSOCIAL_KEYWORDS } from 'api/psychosocialKeyword/queries';
// Types
import { AddKeywordInput, EditKeywordInput } from 'api/graphql-global-types';
import {
  GetAllUserPsychosocialKeywordsVariables,
  GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities,
} from 'api/psychosocialKeyword/types/GetAllUserPsychosocialKeywords';
// Ui
import { successNotification, errorNotification } from 'ui/Notification';

type FormValues = {
  name: string;
};

export type CreateEditPsychosocialKeywordsProps = {
  record: GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities | null;
  hideItemModal: () => void;
  currentAction: string;
  getInput: () => GetAllUserPsychosocialKeywordsVariables;
};

const { Text } = Typography;

const CreateEditPsychosocialKeywords = ({
  record,
  hideItemModal,
  currentAction,
  getInput,
}: CreateEditPsychosocialKeywordsProps): JSX.Element => {
  const initialFormValues: FormValues = {
    name: record?.name || '',
  };

  const [addPsychosocialKeyword, { loading }] = useMutation<AddKeywordInput>(
    ADD_PSYCHOSOCIAL_KEYWORD,
    {
      refetchQueries: [
        {
          query: GET_ALL_PSYCHOSOCIAL_KEYWORDS,
          variables: {
            ...getInput(),
          },
        },
      ],
    }
  );

  const [editPsychosocialKeyword, { loading: updateLoading }] =
    useMutation<EditKeywordInput>(EDIT_PSYCHOSOCIAL_KEYWORD, {
      refetchQueries: [
        {
          query: GET_ALL_PSYCHOSOCIAL_KEYWORDS,
          variables: {
            ...getInput(),
          },
        },
      ],
    });

  const [formFields, setFormFields] = useState<FormValues>(initialFormValues);

  const [form] = Form.useForm();

  const setFields = () => {
    const fields: FormValues = form.getFieldsValue();

    setFormFields({
      ...fields,
    });
  };

  const handleCreate = async () => {
    try {
      await addPsychosocialKeyword({
        variables: {
          input: {
            name: formFields.name,
          },
        },
      });

      successNotification('New item created');
      hideItemModal();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleEdit = async () => {
    try {
      await editPsychosocialKeyword({
        variables: {
          input: {
            id: record?.id,
            name: formFields.name,
          },
        },
      });

      successNotification('Item updated');
      hideItemModal();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleFinish = async () => {
    if (currentAction === 'Edit') {
      await handleEdit();
    } else {
      await handleCreate();
    }
  };

  return (
    <>
      <Form
        form={form}
        layout="horizontal"
        name="createEditPsychosocialKeywords"
        initialValues={initialFormValues}
        autoComplete="off"
        onValuesChange={setFields}
        onLoad={setFields}
        onFinish={handleFinish}
      >
        <div>
          <Form.Item
            name="name"
            label={<Text>Name</Text>}
            help="Name your item"
            rules={[
              {
                required: true,
                message: 'Please enter the name',
              },
            ]}
          >
            <Input placeholder="Example of name" />
          </Form.Item>

          {loading || updateLoading ? (
            <Spin size="large" />
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              className="createEditFormSubmit"
            >
              save
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};

export default CreateEditPsychosocialKeywords;
